import Model from "../../model";
import request from "../../../request/request";
import {
  EnumQuestionType,
  QuestionV2,
  QuestionV2Object
} from "@/lib/model/exambuilder/question-v2";
import Dimensions, { Dimension, DimensionRes } from "@/lib/dimension/dimension";
import {
  BasicInformation,
  ProcessInformation,
  SettingInformation,
  ExamCount,
  ExamItem,
  BasicQuality,
  ObjectiveQuestionScore,
  IseChinese,
  IseEnglish
} from "./exam-v3-sub";
import Auth from "@/lib/common/auth";
import { ExamV2Custom } from "@/lib/model/exambuilder/exam-v2";

// 面试模板结构
export class ExamV3Tem extends Model {
  templateId = 0; // 模版id
  templateName = ""; // 模版名称
  templateRemark = ""; // 模版备注
  coverUrl = ""; // 模版封面
  description = "";
  processInformation = new ProcessInformation(); // 第二大步  流程信息  (包含2.1   2.2   2.3  三小步)
  dimensionList: Dimension[] = [new Dimension()]; // 所有使用的维度(冗余)
  questionList: QuestionV2[] = [new QuestionV2()]; // 所有使用的普通题目(冗余)
  sort = 0; // 排序键
  canUsed = 0; // 当前公司是否可以使用当前模版
  positionId = 0; // 岗位id
  positionName = ""; // 岗位名(冗余)
  getSubmitData() {
    const res = JSON.parse(JSON.stringify(this));
    res.processInformation = this.processInformation.getCleanDefaultData();
    return res;
  }
}

// 面试结构v3  (后端叫重构版)
export default class ExamV3Custom extends ExamV3Tem {
  examId = 0; // 面试ID
  connectCode = ""; // 链接码
  companyId = 0; // 公司id

  basicInformation = new BasicInformation(); // 第一大步 基本信息
  settingInformation = new SettingInformation(); // 第三大步  其他设置信息

  isCandidateUsed = 0; // 是否存在候选人信息 012
  updateType = ""; // 更新类型  basicInformation|processInformation|settingInformation;
}

// CURD
export class ExamV3 extends Model {
  // 接口   /aiExam/examVD/ |get|create|update|delete
  public static get(param: any) {
    // 获取面试信息，注意！！！输入是面试id，返回的是面试最新版本id，后端为了让前端尽量少的改动，字段都叫examId，但是含义是不同的
    return request.post("aiExam/examVD/get", param).then(resp => {
      return new ExamV3Custom().assign(resp.data.data);
    });
  }
  public static create(param: any) {
    // 创建面试
    return request.post("aiExam/examVD/create", param).then(resp => {
      return resp.data;
    });
  }
  public static update(param: ExamV3Custom) {
    // 更新面试
    return request.post("aiExam/examVD/update", param).then(resp => {
      return resp.data;
    });
  }
  public static delete(examId: number, companyId: number) {
    // 删除面试
    return request
      .post("aiExam/examVD/delete", { examId, companyId })
      .then(resp => {
        return resp.data;
      });
  }
  public static copy(param: ExamCopyForm) {
    // 删除面试
    return request.post("aiExam/exam/copy", param).then(resp => {
      return resp.data;
    });
  }
  // 旧面试回显新版面试信息
  static async echoAssgin(exam: ExamV2Custom) {
    const examV3 = new ExamV3Custom();
    examV3.examId = exam.examId;
    examV3.connectCode = exam.connectCode;

    // 基本信息 第一大步
    examV3.basicInformation.name = exam.name;
    examV3.basicInformation.remark = exam.remark;
    examV3.basicInformation.positionId = +exam.positionId;
    examV3.basicInformation.positionName = exam.positionName;
    examV3.basicInformation.templateId = +exam.templateId;

    // 流程信息 第二大步
    try {
      const dimensionParams: any = {
        companyId: Number(Auth.companyId),
        onlyNormalDimension: 1,
        isExamEnv: 0,
        withExamId: exam.examId
      };
      const QuestionParams = {
        companyId: Number(Auth.companyId),
        questionIdList: exam.examScoreConfigList.map(q => q.questionId),
        onlyHasOneDimension: 1,
        isExamEnv: 1, // 是否是面试创建/编辑场景，(历史面试数据兼容需求，写死1)
        withExamId: exam.examId
      };
      const [dList, questionList] = await Promise.all([
        Dimensions.get(dimensionParams),
        QuestionV2Object.get(QuestionParams)
      ]);
      // 基础素质
      examV3.processInformation.basicQualityList = exam.dimensionKeyList.reduce(
        (pre: BasicQuality[], dimensionKey: string) => {
          const target = dList.list.find(d => d.dimensionKey === dimensionKey);
          if (target) {
            const basic = new BasicQuality();
            const scoreConfigList = exam.examScoreConfigList.filter(
              scoreConfig => scoreConfig.dimensionKey === target.dimensionKey
            );
            basic.assign(target);
            basic.questionIdList = scoreConfigList.map(score => {
              const q = questionList.list.find(
                q => q.questionId === score.questionId
              );
              if (q) {
                basic.questionList.push(q);
              }
              return score.questionId;
            });
            if (scoreConfigList[0]) {
              basic.score = scoreConfigList[0].dimensionScore;
            }
            pre.push(basic);
          }
          return pre;
        },
        [] as BasicQuality[]
      );
      examV3.processInformation.objectiveQuestionScoreList = exam.dimensionKeyList.reduce(
        (pre: ObjectiveQuestionScore[], dimensionKey: string) => {
          if (dimensionKey === "自定义客观题") {
            const objectiveQuestionScore = new ObjectiveQuestionScore();
            const scoreConfigList = exam.examScoreConfigList.filter(
              scoreConfig => scoreConfig.dimensionKey === "自定义客观题"
            );
            scoreConfigList.forEach(score => {
              objectiveQuestionScore.questionId = score.questionId;
              objectiveQuestionScore.score = score.dimensionScore;
              pre.push(objectiveQuestionScore);
            });
          }
          return pre;
        },
        [] as ObjectiveQuestionScore[]
      );
      // 知识技能
      const scoreEnglishList = exam.examScoreConfigList.filter(
        scoreConfig => scoreConfig.dimensionKey === "英语"
      );
      if (scoreEnglishList.length) {
        examV3.processInformation.knowledgeAndSkills.iseEnglish.use = 1;
        examV3.processInformation.knowledgeAndSkills.iseEnglish.score =
          scoreEnglishList[0].dimensionScore;
        scoreEnglishList.forEach(scoreEnglish => {
          examV3.processInformation.knowledgeAndSkills.iseEnglish.questionIdList.push(
            scoreEnglish.questionId
          );
          const q = questionList.list.find(
            q => q.questionId === scoreEnglish.questionId
          );
          if (q) {
            examV3.processInformation.knowledgeAndSkills.iseEnglish.questionList.push(
              q
            );
          }
        });
      }

      const scoreChineseList = exam.examScoreConfigList.filter(
        scoreConfig => scoreConfig.dimensionKey === "普通话"
      );
      if (scoreChineseList.length) {
        examV3.processInformation.knowledgeAndSkills.iseChinese.use = 1;
        examV3.processInformation.knowledgeAndSkills.iseChinese.score =
          scoreChineseList[0].dimensionScore;
        scoreChineseList.forEach(scoreChinese => {
          examV3.processInformation.knowledgeAndSkills.iseChinese.questionIdList.push(
            scoreChinese.questionId
          );
          const q = questionList.list.find(
            q => q.questionId === scoreChinese.questionId
          );
          if (q) {
            examV3.processInformation.knowledgeAndSkills.iseChinese.questionList.push(
              q
            );
          }
        });
      }

      if (exam.gameTestConfig[0] && exam.gameTestConfig[0] !== 0) {
        examV3.processInformation.knowledgeAndSkills.gameTestList[0].gameTestType = 1;
        examV3.processInformation.knowledgeAndSkills.gameTestList[0].name =
          "风险偏好小游戏";
      }
    } catch (error) {
      examV3.processInformation.basicQualityList = [];
      examV3.processInformation.objectiveQuestionScoreList = [];
    }
    // 体质仪表
    examV3.processInformation.physicalInstrument.fingerTest.use =
      exam.fingerTest;
    if (exam.fingerTest === 1) {
      examV3.processInformation.physicalInstrument.fingerTest.flexible = 1;
      examV3.processInformation.physicalInstrument.fingerTest.complete = 1;
      examV3.processInformation.physicalInstrument.fingerTest.scar = 1;
    }
    examV3.processInformation.physicalInstrument.colorTest.use = exam.colorTest;

    // 工作匹配
    examV3.processInformation.jobMatching.identityInfo = Number(
      [1, 2, 3, 4].includes(exam.bgCheckProductId)
    );
    examV3.processInformation.jobMatching.socialInfo = Number(
      [2, 4, 6, 8].includes(exam.bgCheckProductId)
    );
    examV3.processInformation.jobMatching.financialRisk = Number(
      [3, 4, 7, 8].includes(exam.bgCheckProductId)
    );

    // 心理动机
    if (exam.psycGaugeConfig.psycGaugeId !== 0) {
      examV3.processInformation.psychologicalMotivation[
        `psyc${exam.psycGaugeConfig.psycGaugeId}`
      ].use = 1;
      examV3.processInformation.psychologicalMotivation[
        `psyc${exam.psycGaugeConfig.psycGaugeId}`
      ].psycGaugeSubDimensionIdList =
        exam.psycGaugeConfig.psycGaugeSubDimensionIdList;
    }

    // 其他设置信息 第三大步
    examV3.settingInformation.timeConfig.examBeginTogether =
      exam.noEnding === 2 ? 1 : 2; //exam.examBeginTogether
    examV3.settingInformation.timeConfig.noEnding = exam.noEnding;
    examV3.settingInformation.timeConfig.beginTime = exam.beginTime;
    examV3.settingInformation.timeConfig.endTime = exam.endTime;
    examV3.settingInformation.timeConfig.timeLimitExam = exam.timeLimitExam;
    if (exam.timeLimitExamType === 2) {
      examV3.settingInformation.timeConfig.timeLimitMinutes =
        exam.timeLimitExam / 60;
      examV3.settingInformation.timeConfig.timeLimitHours = 0;
    } else if (exam.timeLimitExamType === 3) {
      // 固定时间
      const time = Date.parse(exam.endTime) - Date.parse(exam.beginTime);
      examV3.settingInformation.timeConfig.timeLimitExam = parseInt(
        String(time / 1000)
      );
    }
    // examV3.settingInformation.timeConfig.timeLimitHours = exam.timeLimitHours
    // examV3.settingInformation.timeConfig.timeLimitMinutes = exam.timeLimitMinutes
    // examV3.settingInformation.timeConfig.examRegisterEndTime = exam.endTime;
    examV3.settingInformation.timeConfig.examRegisterEndTime = "";

    examV3.settingInformation.virConfig.virVersion = exam.virVersion;
    examV3.settingInformation.paramList = exam.paramList;
    examV3.settingInformation.captchaCheck = exam.captchaCheck;

    examV3.settingInformation.publicityPage.publicityPageContent =
      exam.publicityPageContent;
    examV3.settingInformation.publicityPage.publicityPageVideoUrl =
      exam.publicityPageVideoUrl;
    examV3.settingInformation.publicityPage.publicityPageVideoCoverUrl =
      exam.publicityPageVideoCoverUrl;
    examV3.settingInformation.publicityPage.publicityPageImgUseWhich =
      exam.publicityPageVideoCoverUrl === ""
        ? 0
        : exam.publicityPageVideoCoverUrl ===
          "https://oss.hina.com/app/hina/2021/07/10/3f1396a2ddd6a8493c582f22c69dae39.png"
        ? 1
        : 2; //exam.publicityPageImgUseWhich
    examV3.settingInformation.publicityPage.publicityPageVideoUseWhich =
      exam.publicityPageVideoUseWhich;
    examV3.settingInformation.publicityPage.publicityPageVideoAutoplay =
      exam.publicityPageVideoAutoplay;
    examV3.settingInformation.publicityPage.descBefore = exam.descBefore;
    examV3.settingInformation.publicityPage.descBeforeShowWay =
      exam.descBeforeShowWay;
    examV3.settingInformation.publicityPage.descBeforeShowWayImgUrl =
      exam.descBeforeShowWayImgUrl;
    examV3.settingInformation.publicityPage.descBeforeVideoMustPlay =
      exam.descBeforeVideoMustPlay;
    examV3.settingInformation.publicityPage.descAfter = exam.descAfter;
    examV3.settingInformation.publicityPage.descAfterShowWay =
      exam.descAfterShowWay;
    examV3.settingInformation.publicityPage.descAfterShowWayImgUrl =
      exam.descAfterShowWayImgUrl;
    examV3.settingInformation.publicityPage.descAfterShowWayIsSame = 2;
    // exam.descBeforeShowWay === exam.descAfterShowWay ? 1 : 2; //exam.descAfterShowWayIsSame

    examV3.settingInformation.preventionCheatConfig.screenshotCheck =
      exam.screenshotCheck;
    examV3.settingInformation.preventionCheatConfig.screenshotCheckLimit =
      exam.screenshotCheckLimit;
    examV3.settingInformation.preventionCheatConfig.mobileJoinCountCheck =
      exam.mobileJoinCountCheck;
    examV3.settingInformation.preventionCheatConfig.mobileJoinCountCheckLimit =
      exam.mobileJoinCountCheckLimit;
    examV3.settingInformation.preventionCheatConfig.livenessRecognition =
      exam.livenessRecognition;
    examV3.settingInformation.preventionCheatConfig.lrType = exam.lrType;
    examV3.settingInformation.preventionCheatConfig.replacePersonCheck =
      exam.replacePersonCheck;
    examV3.settingInformation.preventionCheatConfig.faceDetection =
      exam.faceDetection;

    examV3.settingInformation.ruleConfig.isRepeatAnswerLimit =
      exam.repeatAnswer;
    examV3.settingInformation.ruleConfig.repeatAnswerLimit =
      exam.repeatAnswerLimit;
    examV3.settingInformation.ruleConfig.removeMaskTip = exam.removeMaskTip;
    examV3.settingInformation.ruleConfig.canOpenJoin = exam.canOpenJoin;
    examV3.settingInformation.ruleConfig.isNoCreateOpenJoin =
      exam.isNoCreateOpenJoin;

    examV3.settingInformation.experienceConfig.closeQuestionVideo =
      exam.closeQuestionVideo;
    examV3.settingInformation.experienceConfig.wxminiDeviceDetection =
      exam.wxminiDeviceDetection;
    examV3.settingInformation.experienceConfig.wxminiNoiseDetection =
      exam.wxminiNoiseDetection;
    examV3.settingInformation.experienceConfig.testExamId = exam.testExamId;
    examV3.settingInformation.experienceConfig.testExamSwitch =
      exam.testExamId === 0 ? 2 : 1; //exam.testExamSwitch
    return examV3;
  }
}

// 面试列表请求体
export class ExamVDListRequest extends Model {
  companyId = Auth.companyId; // 公司id
  userId = Auth.userId; // 用户id
  examType = "aibuilder"; // 面试类型-新版写死即可
  nameOrRemark = ""; // 名称或备注
  beforeStart = 0; // 未开始
  afterEnd = 0; // 已结束
  isPlaying = 0; // 进行中
  returnAll = false; // 返回全量数据
  page = 0;
  pageSize = 0;
}

export class ExamVDVersionListRequest extends Model {
  companyId = Auth.companyId;
  examId = 0;
  needCandidateCount = true; // 需要统计量
}

// 结束状态
export class ExamVDFinish extends Model {
  all = 0;
  noAudit = 0;
  auditPass = 0;
  auditWait = 0;
  auditRefuse = 0;
}

export class ExamVDCandidateCountRequest extends Model {
  companyId = Auth.companyId;
  examIdList: number[] = [];
}

export class ExamVDCandidateItem extends Model {
  examId = 0;
  all = 0;
  notSignIn = 0;
  signIn = 0;
  answering = 0;
  finish = new ExamVDFinish();

  examVersionId = 0; // 前端字段

  get finishCount() {
    return this.finish.all;
  }
  get beforeStartCount() {
    return this.signIn + this.notSignIn;
  }
  get playingCount() {
    return this.answering;
  }
}

export class ExamVDCandidateCount extends Model {
  list = [new ExamVDCandidateItem()];

  static getCandidateCount(param: ExamVDCandidateCountRequest) {
    return request.post("/aiExam/examVD/candidateCount", param).then(resp => {
      return new ExamVDCandidateCount().assign(resp.data.data);
    });
  }
}

// 面试列表
export class ExamVDList extends Model {
  examCount = new ExamCount();
  list = [new ExamItem()];
  count = 0; // 总计
  page = 0; // 当前分页
  pageSize = 0; // 当前页大小
  pageMax = 0; // 最大有效分页

  static getList(param: ExamVDListRequest) {
    return request.post("/aiExam/examVD/list", param).then(resp => {
      return new ExamVDList().assign(resp.data.data);
    });
  }

  static getVersionList(param: ExamVDVersionListRequest) {
    return request.post("/aiExam/examVD/versionList", param).then(resp => {
      return resp.data;
    });
  }
}

export class ExamCopyForm {
  companyId = 0;
  examId = 0;
  name = "";
  remark = "";
  positionId = 0;
}

// 校验面试结果
export class VerifyExamResponse extends Model {
  isOk = false; // 是否符合重构版
  reason = ""; // 不符合的原因
}
// 校验面试
export class VerifyExamCheck extends Model {
  static getVerifyExam(param: { companyId: number; examId: number }) {
    return request.post("/aiExam/examVD/check", param).then(resp => {
      return new VerifyExamResponse().assign(resp.data.data);
    });
  }
}
